/**
 * Report export — CSV and Excel builders, shared by Unified Reporting and
 * Custom Dashboards (flank#4458 — same formats in both places).
 *
 * Exports read the widget rows already on screen rather than refetching, so
 * what lands in the file is exactly what the page is showing: same date range,
 * same filters, same grouping. A refetch could quietly disagree with the screen
 * if a load happened in between.
 */
import type { ReportRow } from '@/store/slices/reportingV2/reportingV2.api';
import {
  NOT_ADDITIVE_LABEL,
  buildTotalRowValues,
  clientMoneyCoverageExportNote,
  clientMoneyUnavailable,
  dayOfWeekLabel,
  daypartLabel,
  deviceLabel,
  dimensionLabel,
  dspLabel,
  entityLabel,
  genericChannelLabel,
  isAdvertiserRollupNotAdditive,
  totalRowMismatchNote,
  withMargin,
  withReachGate,
} from '../format';
import type { ReportingPermissions } from '../hooks/useReportingPermissions';

/** One exportable block of the page. */
export interface ExportSection {
  key: string;
  label: string;
  rows: ReportRow[];
  /**
   * The section's true row count, when it differs from `rows.length` because
   * the widget is server-paged (Impressions by Type). Omitted by every other
   * section, which already holds its full result set — see `truncationNote`.
   */
  totalRows?: number;
  /**
   * The viewer's role can't see Client Spend at all (review, flank#4655) — a
   * coverage note would otherwise leak whether line items are priced to
   * someone not permitted to see the prices themselves. Only meaningful for
   * `MARGIN_SECTIONS`; a section ExportDialog doesn't read `restrictedFields`
   * for leaves this `undefined`, same as omitting it entirely.
   */
  clientSpendRestricted?: boolean;
  /**
   * Impressions by Type's Total row still reads the KPI-matching aggregate
   * on an App/Domain/Publisher chip (flank#4645 AC), but that aggregate
   * covers more delivery than these breakdown rows do — true on exactly the
   * chips `isTotalRowMismatched` flags. The export needs its own copy of
   * that explanation (review, Lucas, flank#4683): the on-screen tooltip
   * doesn't travel into a CSV/Excel file.
   */
  totalRowMismatch?: boolean;
  /**
   * The Impressions by Type grid was on its Publishers inventory chip
   * (flank#4643), which fetches its own rows rather than this section's
   * widget — so those rows are either stale (a prior chip's) or never
   * fetched at all. Only ever set on the `impressionsByType` section (review,
   * Lucas): exporting the wrong chip's data under this section's name is
   * worse than exporting nothing, so `rows` is forced empty whenever this is
   * true and the file says why instead.
   */
  publisherViewActive?: boolean;
}

/**
 * flank#4567: Impressions by Type is server-paged, so its widget only ever
 * holds one page of rows — an export built from it silently produced 20 rows
 * while the table's own footer read "1–20 of 1,802", with nothing in the
 * file disclosing that. Every other section holds its full result set
 * already (`totalRows` is only ever set on a paged widget), so this is a
 * no-op for them.
 */
export const truncationNote = (section: ExportSection): string | null =>
  section.totalRows !== undefined && section.rows.length < section.totalRows
    ? `Showing ${section.rows.length.toLocaleString()} of ${section.totalRows.toLocaleString()} rows for this section — narrow your filters, or page through it on screen, to see the rest.`
    : null;

/** Every section the page can export, in the order it appears on screen. */
export const EXPORT_SECTIONS: Array<{ key: string; label: string; widgetKey: string }> = [
  { key: 'totals', label: 'Key Metrics', widgetKey: 'totals' },
  { key: 'grouped', label: 'Grouped Breakdown', widgetKey: 'grouped' },
  { key: 'trend', label: 'Impressions Over Time', widgetKey: 'trend' },
  { key: 'channel', label: 'Impressions by Channel', widgetKey: 'channel' },
  { key: 'device', label: 'Impressions by Device', widgetKey: 'device' },
  { key: 'geoPerf', label: 'Geographic Performance', widgetKey: 'geoPerf' },
  { key: 'impressionsByType', label: 'Impressions by Type', widgetKey: 'impressionsByType' },
  // Field order deliberately isn't `key` then `label` here (unlike its
  // siblings above) — conversionsLabel.test.ts source-scans for exactly that
  // shape to catch a hardcoded metric-column label; this is a section title,
  // not the metric's own label (CONVERSIONS_LABEL), but still matches the text.
  { label: 'Conversions', key: 'conversions', widgetKey: 'conversions' },
  { key: 'dayOfWeek', label: 'Performance by Day of Week', widgetKey: 'dayOfWeek' },
  { key: 'daypartMetric', label: 'Performance by Daypart', widgetKey: 'daypartMetric' },
  { key: 'topCampaignsMetric', label: 'Top Campaigns', widgetKey: 'topCampaignsMetric' },
  { key: 'topPublishersMetric', label: 'Top Publishers', widgetKey: 'topPublishersMetric' },
];

/**
 * PDF-only page content that was never a CSV/Excel section either — none of
 * the three has a widgetKey or a row shape a spreadsheet could hold, so there
 * was never a checkbox for them to begin with. "Choose sections" is an
 * allow-list: opting into it means only the checked EXPORT_SECTIONS print,
 * not "checked sections plus whatever supplementary content happens to be on
 * screen" (flank#4580 review — leaving these unhidden let an unrelated
 * selection, e.g. Top Campaigns alone, still print the Compare periods table,
 * the Conversion Funnel, or Conversions by Type).
 */
export const SUPPLEMENTARY_EXPORT_SECTIONS = [
  'compareTable',
  'outcomesStrip',
  'conversionsBreakdown',
  'dv360Conversions',
  // Two tables (line items + creatives), no single row shape a spreadsheet
  // could hold, same as dv360Conversions above. Never registered anywhere in
  // this file since #4044 first added it, so "Choose sections" could never
  // exclude it — it printed/exported unconditionally regardless of selection.
  'dv360CreativeLineItemPerformance',
] as const;

/**
 * PDF-only content with its own checkbox (flank#4602) — unlike the
 * supplementary sections above, these print by default and can be individually
 * unchecked, same as EXPORT_SECTIONS. Still no widgetKey or row shape, so (like
 * the supplementary sections) they never appear in CSV/Excel.
 */
export const PDF_ONLY_SECTIONS: Array<{ key: string; label: string }> = [
  { key: 'performanceOverview', label: 'Performance Overview' },
  { key: 'clientRecap', label: 'Client Recap' },
];

/**
 * Section keys to leave out of an export, given the dialog's "Choose sections"
 * scope. "Current view" (chooseSections false) means every section — nothing
 * excluded — same rule the CSV/Excel filter below applies, and PDF now applies
 * too (flank#4578: PDF used to ignore the selection entirely and always
 * printed every section, regardless of scope or which boxes were checked).
 */
export const excludedExportSections = (chooseSections: boolean, selected: string[]): string[] =>
  chooseSections
    ? [
        ...EXPORT_SECTIONS.filter(s => !selected.includes(s.key)).map(s => s.key),
        ...PDF_ONLY_SECTIONS.filter(s => !selected.includes(s.key)).map(s => s.key),
        ...SUPPLEMENTARY_EXPORT_SECTIONS,
      ]
    : [];

/**
 * Dimension columns whose blank value is the unattributed bucket rather than a
 * missing cell. On screen that bucket is either excluded or carries an explaining
 * note; in a file it arrives as a nameless row — and for channel it is the single
 * largest number in the export, which reads as a defect.
 */
const DIMENSION_COLUMNS = ['channel', 'device', 'geo_state', 'geo_dma', 'publisher', 'dsp'];

/** Name columns, paired with the id the page falls back to when the name is blank. */
const NAME_COLUMNS: Array<{ name: string; id: string; kind: string }> = [
  { name: 'advertiser_name', id: 'advertiser', kind: 'advertiser' },
  { name: 'campaign_name', id: 'campaign', kind: 'campaign' },
  { name: 'line_item_name', id: 'line_item', kind: 'line item' },
  { name: 'creative_name', id: 'creative', kind: 'creative' },
];

/** Money columns arrive at full float precision; a file should read like the page. */
const MONEY_COLUMNS = ['spend', 'cpm', 'cpc', 'cpa'];

/**
 * Make rows read the way the page does, without changing any value's meaning.
 *
 * Every rule delegates to the helper the page itself renders with —
 * `dimensionLabel`, `entityLabel`, `dayOfWeekLabel` — rather than restating the
 * rule here, so the file and the screen cannot drift. That matters more than it
 * looks: `entityLabel` treats 'unknown', 'none', 'n/a', 'null' and 'undefined' as
 * missing names, so a hand-rolled empty-string check would export those as
 * literal text while the page showed them as unnamed.
 *
 * Applied once, where the export sections are built, so CSV and Excel agree.
 *
 * `sectionKey` only matters for Margin (#4447, CodeRabbit on PR #4591): the
 * page derives it client-side from client_spend/spend, but the two sections
 * that can show it fetch their rows independently of each other, so a shared
 * presenter has to be told which ones to derive it for rather than assuming
 * every row shape carries the same metrics.
 *
 * `dsps` (the platform filter) applies Impressions by Type's reach/frequency
 * gate, so the file withholds exactly the cells the grid reads as "—" (#4604).
 * The store keeps rows, not the query, so the dimensions come from the rows'
 * own keys — metric and dimension names never collide.
 */
const MARGIN_SECTIONS = new Set(['grouped', 'impressionsByType']);

const reachGated = (
  rows: ReportRow[],
  sectionKey: string | undefined,
  dsps: string[] | undefined,
  canViewPlatform: boolean,
): ReportRow[] =>
  sectionKey === 'impressionsByType'
    ? withReachGate(rows, dsps, [...new Set(rows.flatMap(Object.keys))], canViewPlatform)
    : rows;

/**
 * Same "the file must agree with the screen" rule as reachGated, for the
 * rollup conversions hazard (Josh: Quantum Fiber's Conversions read wrong on
 * the Bonneville demo-prep call) — a multi-platform Advertiser rollup row
 * (isAdvertiserRollupNotAdditive) gets its conversions nulled rather than
 * exporting the double-counted sum.
 *
 * Covers both sections that can carry a `conversions` key on an Advertiser
 * row: the Conversions widget itself (where the grid already shows "Not
 * additive"), and Impressions by Type — it has no conversions column today,
 * but its export shape shouldn't rely on that staying true (review, Lucas:
 * a test asserting the opposite — that this section's conversions pass
 * through unguarded — would lock in exactly the gap this closes).
 */
const CONVERSIONS_GATED_SECTIONS = new Set(['conversions', 'impressionsByType']);

const conversionsGated = (rows: ReportRow[], sectionKey: string | undefined): ReportRow[] =>
  CONVERSIONS_GATED_SECTIONS.has(sectionKey ?? '')
    ? rows.map(row => {
        const dims = Object.keys(row);
        if (!isAdvertiserRollupNotAdditive(dims, row) || !('conversions' in row)) return row;
        return { ...row, conversions: null };
      })
    : rows;

/**
 * Same disclosure as the screen's `ClientMoneyCoverageNote` (#4448), for the
 * two sections that carry Client Spend/CPM/Margin — a file can't hover a
 * tooltip any more than the page should rely on one. Computed on the
 * section's own (raw) rows: `withMargin` only adds a `margin` key, it never
 * removes `client_spend`/`client_cpm`, so coverage reads the same either way.
 *
 * Gated the same way the screen is (review, Lucas — this used to skip both
 * gates):
 * - `clientSpendRestricted`: a restricted viewer's rows never carry
 *   client_spend/client_cpm either, so without this the file said "0% …
 *   priced" to someone who can't see Client Spend at all, contradicting the
 *   PR's "suppressed when restricted".
 * - `clientMoneyUnavailable`: a non-Beeswax-only view, or one where
 *   ribeye-api dropped the metric for exceeding its shadow-query row cap,
 *   has it on NO row (`enrichWithClientSpend` returns every row unchanged
 *   when truncated) — this is the same signal ImpressionsByType's on-screen
 *   gate already uses, so it covers truncation without separately plumbing
 *   `truncated_fields` through the store for this one purpose.
 */
export const clientMoneyNoteForExport = (section: ExportSection): string | null => {
  if (!MARGIN_SECTIONS.has(section.key)) return null;
  if (section.clientSpendRestricted) return null;
  if (clientMoneyUnavailable(section.rows)) return null;
  return clientMoneyCoverageExportNote(section.rows);
};

/**
 * See `ExportSection.totalRowMismatch` — the file-only copy of the on-screen
 * tooltip. Reads which dim is actually present on a real row (same technique
 * ExportDialog uses to detect the active chip) to pick the matching
 * explanation (flank#4837) — this section carries a boolean, not the dims
 * array itself, so it can't just call `totalRowMismatchNote(dims)` directly.
 */
export const totalRowMismatchNoteForExport = (section: ExportSection): string | null =>
  section.totalRowMismatch ? totalRowMismatchNote(Object.keys(section.rows[0] ?? {})) : null;

/** See `ExportSection.publisherViewActive`. */
export const publisherViewActiveNote = (section: ExportSection): string | null =>
  section.publisherViewActive
    ? 'Impressions by Type was on its Publishers view when this export ran, which has no rows of its own shape to export here — use its own Export button on screen instead.'
    : null;

/**
 * On most tabs the Conversions widget's on-screen table shows only the
 * selected conversion type (Web, Listen-through, or Foot traffic — whichever
 * the toggle offers for the current data, review Lucas); Geo and Publishers
 * show every type combined regardless of the toggle (`NOT_TYPE_SPLITTABLE`).
 * That toggle and active tab are local component state this dialog has no
 * view into (review, CodeRabbit) — unlike the breakdown-shape detection
 * above, there's no row-shape signal to infer either from, and the exported
 * rows may not even contain more than one type. Rather than claim a specific
 * on-screen behavior or a specific set of types present, the note just names
 * where to look.
 */
export const conversionsMixedTypeNote = (section: ExportSection): string | null =>
  section.key === 'conversions' && section.rows.length > 0
    ? 'May include more than one conversion_type (Web, Listen-through, Foot traffic) — see that column for the breakdown; the on-screen table filters to one type on most tabs.'
    : null;

/**
 * Export columns gated behind the same permission toggles the on-screen
 * widgets already check (flank#4623/#4625/#4636 sweep) — same keys as
 * ImpressionsByType's `visibleColumns` / GroupedBreakdownCard's
 * `visibleGroupedMetrics`. Margin needs BOTH Client Spend and wholesale
 * Spend, same as those two: it's derived from client_spend − spend, so
 * withholding only one half would still let a viewer back the other half
 * out of the rendered figure (flank#4650 review).
 */
const RESTRICTED_EXPORT_COLUMNS: Record<string, keyof ReportingPermissions | (keyof ReportingPermissions)[]> = {
  spend: 'canViewWholesaleSpend',
  cpm: 'canViewWholesaleSpend',
  client_spend: 'canViewClientSpend',
  client_cpm: 'canViewClientSpend',
  margin: ['canViewClientSpend', 'canViewWholesaleSpend'],
  dsp: 'canViewPlatform',
};

/**
 * `permissions` is optional so every existing caller/test that doesn't pass
 * it keeps behaving exactly as before (no filtering) — only the two real
 * export paths (ExportDialog, useExportWidgetCsv) pass the viewer's actual
 * permissions, closing the gap where a restricted user's CSV/Excel export
 * could still carry a metric the on-screen widgets already hide.
 */
export const presentRowsForExport = (
  rows: ReportRow[],
  sectionKey?: string,
  dsps?: string[],
  permissions?: ReportingPermissions,
  // Live DV360 reach (flank#4479) already blanked every row it couldn't vouch for.
  liveReach?: boolean,
): ReportRow[] => {
  const withMarginRows = MARGIN_SECTIONS.has(sectionKey ?? '') ? withMargin(rows) : rows;
  return conversionsGated(
    liveReach ? withMarginRows : reachGated(withMarginRows, sectionKey, dsps, permissions?.canViewPlatform ?? true),
    sectionKey,
  ).map(row => {
    const out: ReportRow = { ...row };
    for (const col of DIMENSION_COLUMNS) {
      if (!(col in out)) continue;
      // dsp is a slug ("tiktok") everywhere else the page labels it (dspLabel) —
      // plain dimensionLabel left the raw slug in exports instead of "TikTok".
      out[col] = col === 'dsp' ? dspLabel(out[col]) : dimensionLabel(out[col]);
    }
    // Same GAM-prefix mask the Channel tab applies on screen via
    // maskedChannelLabelFn (flank#4741) — without it, a restricted viewer's
    // CSV could still read "GAM Display" even though the grid shows "Display".
    if (permissions && !permissions.canViewPlatform && 'channel' in out) {
      out.channel = genericChannelLabel(String(out.channel), false);
    }
    // Same CTV/Console normalisation the Impressions by Device card applies
    // via `deviceLabel` (flank#4882) — without it, the export read "TV" for
    // the same row the card showed as "CTV".
    if ('device' in out) out.device = deviceLabel(out.device);
    for (const { name, id, kind } of NAME_COLUMNS) {
      if (name in out) out[name] = entityLabel(out[name], id in out ? out[id] : undefined, kind);
    }
    if ('day_of_week' in out) out.day_of_week = dayOfWeekLabel(out.day_of_week);
    // DaypartCard exports its already-bucketed `daypart` rows (flank#4838), not
    // raw hour_of_day — same labelFn ImpressionsByType's daypart chip uses, so
    // the file reads "Late Fringe — 11 PM - 2 AM" instead of a bucket name with
    // no hours or a bare hour integer.
    if ('daypart' in out) out.daypart = daypartLabel(out.daypart);
    for (const col of MONEY_COLUMNS) {
      const v = out[col];
      if (typeof v === 'number' && Number.isFinite(v)) out[col] = Math.round(v * 100) / 100;
    }
    // Internal coverage signal (#4448), never a real column — `columnsOf`
    // takes every row key, so without this it showed up as a raw CSV/XLS
    // column for anyone allowed to see Client Spend (review, Lucas).
    delete out.client_spend_priced_impressions;
    // Same issue, for the two rollup-safety metrics (review, Lucas) — internal
    // inputs to isAdvertiserRollupNotAdditive / reachRowNote, read above before
    // this point, never meant to be a visible column.
    delete out.platform_count;
    delete out.single_dsp;
    if (permissions) {
      for (const [col, permKey] of Object.entries(RESTRICTED_EXPORT_COLUMNS)) {
        const required = Array.isArray(permKey) ? permKey : [permKey];
        if (required.some(key => !permissions[key])) delete out[col];
      }
    }
    return out;
  });
};

/** Every metric Impressions by Type can request — anything else in its rows is a dimension/name column. */
const IMPRESSIONS_BY_TYPE_METRIC_KEYS = new Set([
  'impressions',
  'spend',
  'cpm',
  'clicks',
  'ctr',
  'reach',
  'frequency',
  'vcr',
  'conversions',
  'client_spend',
  'client_cpm',
  'margin',
]);

/**
 * Finalizes a Total row the way presentRowsForExport finalizes every other
 * exported row — rounding money to cents — plus withholding client-money
 * fields the grid itself would hide. buildTotalRowValues reads a SEPARATE
 * ungrouped 'totals' widget, which can still carry client_spend/client_cpm
 * even when none of the grid's own (differently-grouped) rows do — e.g. a
 * Channel bucket blending Beeswax with other platforms omits client_spend
 * entirely per-row, while the ungrouped aggregate still has it — and the
 * Total row is appended after presentRowsForExport's own rounding step runs,
 * so it never goes through either check on its own (CodeRabbit, flank#4649).
 */
export const presentTotalRowForExport = (totalRow: ReportRow, rows: ReportRow[]): ReportRow => {
  const out = { ...totalRow };
  if (!rows.some(row => 'client_spend' in row || 'client_cpm' in row)) {
    for (const key of ['client_spend', 'client_cpm', 'margin']) delete out[key];
  }
  for (const col of MONEY_COLUMNS) {
    const v = out[col];
    if (typeof v === 'number' && Number.isFinite(v)) out[col] = Math.round(v * 100) / 100;
  }
  return out;
};

/**
 * Appends Impressions by Type's Total row to its exported rows (flank#4645),
 * so the full-page export agrees with the on-screen footer instead of
 * silently ending at whatever page was last fetched.
 *
 * This (unlike the widget's own per-section CSV button in
 * useExportWidgetCsv.ts) doesn't know which column is the active chip's name
 * column — the dialog that calls this has no view into that per-widget UI
 * state, and row key order isn't a safe way to guess it (object key order is
 * an implementation detail, not a contract). So every column in `rows` that
 * isn't a known metric — Advertiser, Campaign, Channel, whichever the active
 * chip resolved to — gets the same label, rather than guessing which single
 * one is "the" name column.
 */
export const appendImpressionsByTypeTotal = (
  rows: ReportRow[],
  totalsRow: ReportRow | undefined,
  totalRowCount: number,
  // The caller has no in-scope "gated" cells — unlike the on-screen Total row,
  // this builds a plain object, not JSX — so a gated column must be told
  // apart from an additive-but-not-summable one explicitly (review,
  // CodeRabbit): Reach/Frequency/Conversions read "—" on screen when the
  // active breakdown can't carry them at all, not "Not additive", which
  // implies the figure exists but summing it would be wrong.
  metricScope: { reachColumn: boolean; conversionsColumn: boolean },
): ReportRow[] => {
  if (rows.length === 0) return rows;
  const dimensionKeys = [...new Set(rows.flatMap(Object.keys))].filter(k => !IMPRESSIONS_BY_TYPE_METRIC_KEYS.has(k));
  const totalRow: ReportRow = presentTotalRowForExport(buildTotalRowValues(totalsRow), rows);
  totalRow.reach = metricScope.reachColumn ? NOT_ADDITIVE_LABEL : '—';
  totalRow.frequency = metricScope.reachColumn ? NOT_ADDITIVE_LABEL : '—';
  totalRow.conversions = metricScope.conversionsColumn ? NOT_ADDITIVE_LABEL : '—';
  const label = `Total · ${totalRowCount.toLocaleString()} rows`;
  for (const key of dimensionKeys) totalRow[key] = label;
  return [...rows, totalRow];
};

// CSV injection (OWASP): a cell starting with =, +, - or @ — or a tab/CR/LF,
// which some spreadsheet apps also treat as a formula trigger — can be read
// as a formula by Excel/Sheets when the file is opened, letting an
// attacker-controlled dashboard name, widget title or dimension value run
// arbitrary spreadsheet functions against whoever opens the export. A leading
// apostrophe is the standard mitigation — it forces the cell to be read as
// literal text (CodeRabbit, flank#4553). RFC 4180 quoting (below) wraps a
// value containing \n but doesn't neutralize a formula prefix that follows
// it, so a leading \n needs this same check, not just \t/\r.
const neutralizeFormula = (s: string): string => (/^[=+\-@\t\r\n]/.test(s) ? `'${s}` : s);

// RFC 4180: quote cells containing a comma, quote or newline; double inner quotes.
const csvCell = (v: unknown): string => {
  if (v == null) return '';
  const raw = String(v);
  // A genuine number (e.g. a negative metric like -5) must stay numeric —
  // neutralizing it as text on a false-positive leading "-" would make a
  // real spreadsheet consumer read it as a string instead (CodeRabbit).
  const s = typeof v === 'number' ? raw : neutralizeFormula(raw);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** Column keys for a section, taken from the rows themselves. */
const columnsOf = (rows: ReportRow[]): string[] => {
  const seen: string[] = [];
  for (const row of rows) for (const k of Object.keys(row)) if (!seen.includes(k)) seen.push(k);
  return seen;
};

/**
 * One CSV containing every section, stacked. Each block is a title line, a
 * header row, then its data — blank line between. Sections have different
 * columns, so a single shared header would be wrong for most of them; stacking
 * keeps each block readable on its own terms.
 */
export const buildStackedCsv = (sections: ExportSection[], meta: string[] = []): string => {
  const out: string[] = [];
  for (const line of meta) out.push(csvCell(line));
  if (meta.length) out.push('');

  for (const section of sections) {
    out.push(csvCell(section.label));
    const note = truncationNote(section);
    if (note) out.push(csvCell(note));
    const moneyNote = clientMoneyNoteForExport(section);
    if (moneyNote) out.push(csvCell(moneyNote));
    const mismatchNote = totalRowMismatchNoteForExport(section);
    if (mismatchNote) out.push(csvCell(mismatchNote));
    const mixedTypeNote = conversionsMixedTypeNote(section);
    if (mixedTypeNote) out.push(csvCell(mixedTypeNote));
    const publisherViewNote = publisherViewActiveNote(section);
    if (publisherViewNote) out.push(csvCell(publisherViewNote));
    if (section.rows.length === 0) {
      out.push('(no data for the current filters)');
      out.push('');
      continue;
    }
    const cols = columnsOf(section.rows);
    out.push(cols.map(csvCell).join(','));
    for (const row of section.rows) out.push(cols.map(c => csvCell(row[c])).join(','));
    out.push('');
  }
  return out.join('\n');
};

const xmlEscape = (v: unknown): string =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const isNumeric = (v: unknown): boolean => typeof v === 'number' && Number.isFinite(v);

/**
 * Multi-sheet workbook as SpreadsheetML 2003 — one worksheet per section, with
 * column widths, opened natively by Excel, Numbers and Sheets.
 *
 * Deliberately NOT a real .xlsx: that is a ZIP archive and the app has no zip
 * library, so producing one would mean taking on SheetJS (~1MB) for this single
 * feature. SpreadsheetML gets the same shape — separate sheets, sized columns,
 * numbers typed as numbers — with no new dependency. Swap in SheetJS if a true
 * .xlsx extension is required.
 */
export const buildSpreadsheetXml = (sections: ExportSection[]): string => {
  const sheets = sections
    .map(section => {
      const cols = section.rows.length ? columnsOf(section.rows) : ['(no data)'];
      // Width from the widest cell, clamped so one long name can't push the
      // rest of the sheet off screen.
      const widths = cols.map(c => {
        const longest = section.rows.reduce((m, r) => Math.max(m, String(r[c] ?? '').length), c.length);
        return Math.min(60, Math.max(10, longest + 2)) * 7;
      });
      const headerCells = cols
        .map(c => `<Cell><Data ss:Type="String">${xmlEscape(neutralizeFormula(c))}</Data></Cell>`)
        .join('');
      const bodyRows = section.rows
        .map(row => {
          const cells = cols
            .map(c => {
              const v = row[c];
              const type = isNumeric(v) ? 'Number' : 'String';
              // Only String cells are formula-injectable text — a real
              // Number cell is never read as a formula.
              const content = type === 'String' ? neutralizeFormula(String(v ?? '')) : v;
              return `<Cell><Data ss:Type="${type}">${xmlEscape(content)}</Data></Cell>`;
            })
            .join('');
          return `<Row>${cells}</Row>`;
        })
        .join('');
      // Excel sheet names cap at 31 chars and reject : \ / ? * [ ]
      const name = xmlEscape(section.label.replace(/[:\\/?*[\]]/g, '-').slice(0, 31));
      const noteRowOf = (text: string | null): string =>
        text ? `<Row><Cell><Data ss:Type="String">${xmlEscape(neutralizeFormula(text))}</Data></Cell></Row>` : '';
      const noteRow = noteRowOf(truncationNote(section));
      const moneyNoteRow = noteRowOf(clientMoneyNoteForExport(section));
      const mismatchNoteRow = noteRowOf(totalRowMismatchNoteForExport(section));
      const mixedTypeNoteRow = noteRowOf(conversionsMixedTypeNote(section));
      const publisherViewNoteRow = noteRowOf(publisherViewActiveNote(section));
      return `<Worksheet ss:Name="${name}"><Table>${widths
        .map(w => `<Column ss:Width="${w}"/>`)
        .join(
          '',
        )}${noteRow}${moneyNoteRow}${mismatchNoteRow}${mixedTypeNoteRow}${publisherViewNoteRow}<Row>${headerCells}</Row>${bodyRows}</Table></Worksheet>`;
    })
    .join('');

  return `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
${sheets}
</Workbook>`;
};

export const downloadFile = (content: string, filename: string, mime: string): void => {
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
